import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Calendar, 
  Mail, 
  Play, 
  Pause, 
  RefreshCw, 
  Clock, 
  Users, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Send,
  Settings,
  History,
  Building,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WeeklyReportsPage() {
  const [status, setStatus] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [history, setHistory] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Offers & Orders state
  const [offersOrdersData, setOffersOrdersData] = useState(null);
  const [offersOrdersLoading, setOffersOrdersLoading] = useState(false);
  const [offersOrdersSending, setOffersOrdersSending] = useState(false);
  const [offersOrdersHistory, setOffersOrdersHistory] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchOffersOrdersPreview = useCallback(async () => {
    setOffersOrdersLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/offers-orders/preview`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setOffersOrdersData(data);
    } catch (err) {
      console.error('Error fetching offers/orders preview:', err);
    } finally {
      setOffersOrdersLoading(false);
    }
  }, []);

  const fetchOffersOrdersHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/offers-orders/history`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setOffersOrdersHistory(data.history || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, []);

  const sendOffersOrdersReport = async () => {
    setOffersOrdersSending(true);
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/offers-orders/send`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.status === 'sent') {
        toast.success(`Report sent to ${data.sent_to.join(', ')}`);
        fetchOffersOrdersHistory();
      } else {
        toast.error('Failed to send report');
      }
    } catch (err) {
      toast.error('Error sending report');
    } finally {
      setOffersOrdersSending(false);
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/scheduler/status`);
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/subscriptions`);
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/scheduler/history`);
      const data = await res.json();
      setHistory(data);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/logs?limit=20`);
      const data = await res.json();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchSubscriptions(), fetchHistory(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchSubscriptions, fetchHistory, fetchLogs]);

  // Load offers/orders data when tab is selected
  useEffect(() => {
    if (activeTab === 'offers-orders') {
      fetchOffersOrdersPreview();
      fetchOffersOrdersHistory();
    }
  }, [activeTab, fetchOffersOrdersPreview, fetchOffersOrdersHistory]);

  const toggleTestMode = async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/toggle-test-mode`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message);
      fetchStatus();
    } catch (err) {
      toast.error('Failed to toggle test mode');
    }
  };

  const toggleScheduler = async (enable) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      const res = await fetch(`${API_URL}/api/weekly-reports/scheduler/${endpoint}`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message);
      fetchStatus();
    } catch (err) {
      toast.error('Failed to toggle scheduler');
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/scheduler/run-now`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message);
      await Promise.all([fetchStatus(), fetchLogs(), fetchHistory()]);
    } catch (err) {
      toast.error('Failed to run scheduler');
    } finally {
      setRunning(false);
    }
  };

  const generateSubscriptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/generate-from-pfs`, { method: 'POST' });
      const data = await res.json();
      toast.success(`Created ${data.created} subscriptions, ${data.skipped} skipped`);
      fetchSubscriptions();
      fetchStatus();
    } catch (err) {
      toast.error('Failed to generate subscriptions');
    }
  };

  const toggleSubscription = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/subscriptions/${id}/toggle`, { method: 'PUT' });
      const data = await res.json();
      toast.success(data.message);
      fetchSubscriptions();
      fetchStatus();
    } catch (err) {
      toast.error('Failed to toggle subscription');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar />
      <div className="p-6 space-y-6" data-testid="weekly-reports-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Weekly Shipment Reports</h1>
            <p className="text-gray-500">Automated weekly reports sent to customers every Sunday at 8:00 AM CET</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => Promise.all([fetchStatus(), fetchSubscriptions(), fetchLogs()])}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Scheduler</p>
                <p className="text-2xl font-bold">{status?.enabled ? 'Enabled' : 'Disabled'}</p>
              </div>
              <div className={`p-3 rounded-full ${status?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                {status?.enabled ? <Play className="w-6 h-6 text-green-600" /> : <Pause className="w-6 h-6 text-gray-400" />}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Test Mode</p>
                <p className="text-2xl font-bold">{status?.test_mode ? 'ON' : 'OFF'}</p>
              </div>
              <div className={`p-3 rounded-full ${status?.test_mode ? 'bg-amber-100' : 'bg-blue-100'}`}>
                <AlertTriangle className={`w-6 h-6 ${status?.test_mode ? 'text-amber-600' : 'text-blue-600'}`} />
              </div>
            </div>
            {status?.test_mode && (
              <p className="text-xs text-amber-600 mt-2">All emails go to: {status?.test_email}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Subscriptions</p>
                <p className="text-2xl font-bold">{status?.subscriptions?.active || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">{status?.subscriptions?.total || 0} total companies</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Next Run</p>
                <p className="text-lg font-bold">
                  {status?.next_run_cet ? new Date(status.next_run_cet).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '-'}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Sunday 08:00 CET</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['overview', 'subscriptions', 'offers-orders', 'logs'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {tab === 'offers-orders' ? 'Offers & Orders' : tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Scheduler Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Scheduler Enabled</p>
                  <p className="text-sm text-gray-500">Automatically send reports every Sunday</p>
                </div>
                <Switch checked={status?.enabled} onCheckedChange={toggleScheduler} />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Test Mode</p>
                  <p className="text-sm text-gray-500">Send all emails to {status?.test_email || 'rok.kobal@europacific.com'}</p>
                </div>
                <Switch checked={status?.test_mode} onCheckedChange={toggleTestMode} />
              </div>

              <div className="pt-4 border-t">
                <Button 
                  onClick={runNow} 
                  disabled={running}
                  className="w-full"
                  data-testid="run-now-btn"
                >
                  {running ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending Reports...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Run Now (Send All Reports)</>
                  )}
                </Button>
                {status?.test_mode && (
                  <p className="text-xs text-amber-600 text-center mt-2">
                    Test mode ON: All emails will go to {status?.test_email}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history?.daily_stats?.length > 0 ? (
                <div className="space-y-3">
                  {history.daily_stats.slice(0, 5).map((stat, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{stat.date}</p>
                        <p className="text-sm text-gray-500">{stat.companies_count} companies</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {stat.sent}
                        </Badge>
                        {stat.failed > 0 && (
                          <Badge variant="outline" className="bg-red-50 text-red-700">
                            <XCircle className="w-3 h-3 mr-1" />
                            {stat.failed}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No report history yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'subscriptions' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Company Subscriptions
              </CardTitle>
              <Button variant="outline" onClick={generateSubscriptions}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Generate from PFs
              </Button>
            </div>
            <CardDescription>
              Companies receiving weekly shipment reports
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Company</th>
                    <th className="text-left py-3 px-4">Emails</th>
                    <th className="text-left py-3 px-4">PF Count</th>
                    <th className="text-left py-3 px-4">Last Sent</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{sub.company_name || sub.customer_name}</td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          {(sub.emails || [sub.email]).slice(0, 2).map((e, i) => (
                            <div key={i} className="text-gray-600">{e}</div>
                          ))}
                          {(sub.emails?.length || 0) > 2 && (
                            <div className="text-gray-400">+{sub.emails.length - 2} more</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">{sub.pf_count || '-'}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {sub.last_sent ? new Date(sub.last_sent).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={sub.is_active ? "default" : "secondary"}>
                          {sub.is_active ? 'Active' : 'Paused'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleSubscription(sub.id)}
                        >
                          {sub.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscriptions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No subscriptions yet. Click "Generate from PFs" to create subscriptions from Position Folders.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Send Logs
            </CardTitle>
            <CardDescription>
              Recent report emails sent
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Company</th>
                    <th className="text-left py-3 px-4">Sent To</th>
                    <th className="text-left py-3 px-4">PFs</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">
                        {log.sent_at ? new Date(log.sent_at).toLocaleString('en-GB', { 
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                        }) : '-'}
                      </td>
                      <td className="py-3 px-4 font-medium">{log.company_name || '-'}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{log.email}</td>
                      <td className="py-3 px-4">{log.summary?.position_folders || log.summary?.shipments || '-'}</td>
                      <td className="py-3 px-4">
                        {log.success ? (
                          <Badge className="bg-green-100 text-green-700">Sent</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Failed</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {log.test_mode && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700">Test</Badge>
                        )}
                        {log.is_test_send && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">Manual</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {logs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No email logs yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Offers & Orders Tab */}
      {activeTab === 'offers-orders' && (
        <div className="space-y-6">
          {/* Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📊 Weekly Offers & Orders Report
              </CardTitle>
              <CardDescription>
                Internal management report - sent to rok.kobal@europacific.com (test mode)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Button 
                  onClick={fetchOffersOrdersPreview} 
                  disabled={offersOrdersLoading}
                  variant="outline"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${offersOrdersLoading ? 'animate-spin' : ''}`} />
                  Refresh Data
                </Button>
                <Button 
                  onClick={sendOffersOrdersReport} 
                  disabled={offersOrdersSending}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className={`w-4 h-4 mr-2 ${offersOrdersSending ? 'animate-spin' : ''}`} />
                  {offersOrdersSending ? 'Sending...' : 'Send Report'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Data */}
          {offersOrdersData && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Offers by Day */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">📧 Offers Sent</CardTitle>
                  <CardDescription>Week: {offersOrdersData.week_label}</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Sources */}
                  <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                    <div className="bg-gray-50 p-2 rounded text-center">
                      <div className="font-bold text-blue-600">{offersOrdersData.offers_stats?.email_archive_count || 0}</div>
                      <div className="text-gray-500">Email Archive</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded text-center">
                      <div className="font-bold text-green-600">{offersOrdersData.offers_stats?.system_offers_count || 0}</div>
                      <div className="text-gray-500">System Offers</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {offersOrdersData.offers_stats?.by_day?.map((day, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b">
                        <span className="text-sm">{day.day}</span>
                        <span className="font-medium">{day.count}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 bg-blue-50 px-2 rounded font-bold">
                      <span>Total</span>
                      <span>{offersOrdersData.offers_stats?.total || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* PFs / Orders */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">📦 Position Folders</CardTitle>
                  <CardDescription>Orders this week</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <div className="text-2xl font-bold text-blue-600">{offersOrdersData.pf_stats?.total || 0}</div>
                      <div className="text-xs text-gray-500">Total PFs</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <div className="text-2xl font-bold text-green-600">{offersOrdersData.pf_stats?.new_orders || 0}</div>
                      <div className="text-xs text-gray-500">New Orders</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <div className="text-2xl font-bold">{offersOrdersData.pf_stats?.by_status?.active || 0}</div>
                      <div className="text-xs text-gray-500">Active</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded text-center">
                      <div className="text-2xl font-bold">{offersOrdersData.pf_stats?.by_status?.closed || 0}</div>
                      <div className="text-xs text-gray-500">Closed</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Reactivation Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">🔄 Customer Reactivation</CardTitle>
                  <CardDescription>Campaign performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm">Companies Contacted</span>
                      <span className="font-medium">{offersOrdersData.reactivation_stats?.contacted || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Emails Sent</span>
                      <span className="font-medium">{offersOrdersData.reactivation_stats?.emails_sent || 0}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span className="text-sm">Bounced</span>
                      <span className="font-medium">{offersOrdersData.reactivation_stats?.bounced || 0}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span className="text-sm">Responded</span>
                      <span className="font-medium">{offersOrdersData.reactivation_stats?.responses || 0}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between">
                      <span className="text-sm">Vouchers Created</span>
                      <span className="font-medium">{offersOrdersData.reactivation_stats?.vouchers_created || 0}</span>
                    </div>
                    <div className="flex justify-between text-green-700 font-bold">
                      <span className="text-sm">Vouchers Redeemed</span>
                      <span>{offersOrdersData.reactivation_stats?.vouchers_redeemed || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Send History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Send History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {offersOrdersHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No reports sent yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-left py-2 px-3">Week</th>
                        <th className="text-left py-2 px-3">Recipients</th>
                        <th className="text-left py-2 px-3">PFs</th>
                        <th className="text-left py-2 px-3">Offers</th>
                        <th className="text-left py-2 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offersOrdersHistory.map((log, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3">
                            {new Date(log.sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-2 px-3">{log.week_start?.slice(0, 10) || '-'}</td>
                          <td className="py-2 px-3">{log.recipients?.join(', ') || '-'}</td>
                          <td className="py-2 px-3">{log.data_summary?.pf_count || '-'}</td>
                          <td className="py-2 px-3">{log.data_summary?.offers_count || '-'}</td>
                          <td className="py-2 px-3">
                            {log.sent_count > 0 ? (
                              <Badge className="bg-green-100 text-green-700">Sent</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Failed</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </div>
  );
}
